import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import PropertyCard from '../components/PropertyCard';
import { getAllProperties } from '../services/propertyService';
import { Property } from '../types';

const PropertiesPage: React.FC = () => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Filter states
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    minBedrooms: '',
    minBathrooms: '',
    propertyType: '',
    location: ''
  });

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const allProperties = await getAllProperties();
        
        // Apply any filters from URL params
        let filteredProperties = allProperties;
        
        // Apply location filter if it exists in the URL
        const locationParam = searchParams.get('location');
        if (locationParam) {
          setFilters(prev => ({ ...prev, location: locationParam }));
          filteredProperties = filteredProperties.filter(prop => 
            prop.location.toLowerCase().includes(locationParam.toLowerCase())
          );
        }
        
        // Apply property type filter if it exists in the URL
        const typeParam = searchParams.get('type');
        if (typeParam) {
          setFilters(prev => ({ ...prev, propertyType: typeParam }));
          filteredProperties = filteredProperties.filter(prop => 
            prop.type.toLowerCase() === typeParam.toLowerCase()
          );
        }
        
        setProperties(filteredProperties);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProperties();
  }, [searchParams]);
  
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };
  
  const applyFilters = () => {
    let filteredProperties = properties;
    
    if (filters.minPrice) {
      filteredProperties = filteredProperties.filter(prop => prop.price >= Number(filters.minPrice));
    }
    
    if (filters.maxPrice) {
      filteredProperties = filteredProperties.filter(prop => prop.price <= Number(filters.maxPrice));
    }
    
    if (filters.minBedrooms) {
      filteredProperties = filteredProperties.filter(prop => prop.bedrooms >= Number(filters.minBedrooms));
    }
    
    if (filters.minBathrooms) {
      filteredProperties = filteredProperties.filter(prop => prop.bathrooms >= Number(filters.minBathrooms));
    }
    
    if (filters.propertyType) {
      filteredProperties = filteredProperties.filter(prop => 
        prop.type.toLowerCase() === filters.propertyType.toLowerCase()
      );
    }
    
    if (filters.location) {
      filteredProperties = filteredProperties.filter(prop => 
        prop.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    
    setProperties(filteredProperties);
  };
  
  const resetFilters = async () => {
    setFilters({
      minPrice: '',
      maxPrice: '',
      minBedrooms: '',
      minBathrooms: '',
      propertyType: '',
      location: ''
    });
    
    // Clear URL params
    navigate('/properties');
    
    // Reset to all properties
    const allProperties = await getAllProperties();
    setProperties(allProperties);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-cover bg-center h-72" style={{ 
        backgroundImage: 'url(https://images.unsplash.com/photo-1546593064-053d21199be1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80)' 
      }}>
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="container-custom mx-auto h-full flex flex-col items-center justify-center relative z-10 px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white text-center">
            Explore Our Properties
          </h1>
          <p className="text-xl text-white text-center mt-4 max-w-2xl">
            Find your dream property in Costa Rica's most beautiful locations
          </p>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container-custom mx-auto py-12 px-4">
        {/* Filters Section */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Filter Properties</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="minPrice" className="block text-sm font-medium text-gray-700">Min Price</label>
              <input
                type="number"
                name="minPrice"
                id="minPrice"
                value={filters.minPrice}
                onChange={handleFilterChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="maxPrice" className="block text-sm font-medium text-gray-700">Max Price</label>
              <input
                type="number"
                name="maxPrice"
                id="maxPrice"
                value={filters.maxPrice}
                onChange={handleFilterChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="minBedrooms" className="block text-sm font-medium text-gray-700">Min Bedrooms</label>
              <input
                type="number"
                name="minBedrooms"
                id="minBedrooms"
                value={filters.minBedrooms}
                onChange={handleFilterChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="minBathrooms" className="block text-sm font-medium text-gray-700">Min Bathrooms</label>
              <input
                type="number"
                name="minBathrooms"
                id="minBathrooms"
                value={filters.minBathrooms}
                onChange={handleFilterChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label htmlFor="propertyType" className="block text-sm font-medium text-gray-700">Property Type</label>
              <select
                name="propertyType"
                id="propertyType"
                value={filters.propertyType}
                onChange={handleFilterChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">All</option>
                <option value="house">House</option>
                <option value="apartment">Apartment</option>
                <option value="condo">Condo</option>
              </select>
            </div>
            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700">Location</label>
              <input
                type="text"
                name="location"
                id="location"
                value={filters.location}
                onChange={handleFilterChange}
                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={applyFilters}
              className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Apply Filters
            </button>
            <button
              onClick={resetFilters}
              className="inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Properties Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-3 text-center">Loading...</div>
          ) : (
            properties.map(property => (
              <PropertyCard key={property.id} property={property} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertiesPage;